import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import ProfileCard from './ProfileCard';
import StatsCard from './StatsCard';
import TeamCard from './TeamCard';
import QuickActionsBar from './QuickActionsBar';
import CommunityCanvas from '../components/CommunityCanvas';
import ScheduleCard from '../components/ScheduleCard';
// PerformanceCharts removed: the trends section has been deprecated per user request.

function TherapistDashboard() {
  const navigate = useNavigate();

  // THERAPIST STATS
  const [sessionStats, setSessionStats] = useState({
    lastWeekCount: 0,
    thisWeekCount: 0,
    monthlyAvgCount: 0,
    debtCount: 0,
    debtTotal: 0,
    noShowWeekCount: 0,
    noShowMonthCount: 0,
    cancelWeekCount: 0,
    cancelMonthCount: 0
  });

  // GOALS
  const [goals, setGoals] = useState({
    weekly_session_goal: 0,
    weekly_session_bonus: 0
  });

  // THERAPIST NAME
  const [therapistName, setTherapistName] = useState('');

  // THERAPIST PROFILE
  const [profile, setProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cardTarget, setCardTarget] = useState(null);

  // TEAM MEMBERS
  const [teamMembers, setTeamMembers] = useState([]);

  // Unread admin messages count
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch the unread messages count for this therapist
  const fetchUnreadCount = async () => {
    try {
      const res = await API.get('/therapist/messages/unread_count');
      const count = res.data && typeof res.data.count === 'number' ? res.data.count : 0;
      setUnreadCount(count);
    } catch (err) {
      console.error('Error fetching unread message count:', err);
    }
  };

  // The trends / performance charts section has been removed as it
  // provided limited value and has been deprecated.  If you need to
  // reintroduce charts in the future, you can create a dedicated
  // component and fetch data as needed.

  // --------------------------------------------------------------------------
  // FETCH STATS & GOALS
  // --------------------------------------------------------------------------
  const fetchSessionStats = async () => {
    try {
      const res = await API.get('/therapist/stats');
      const data = res.data || {};
      data.debtTotal = parseFloat(data.debtTotal) || 0;
      setSessionStats(data);
    } catch (error) {
      console.error('Error fetching session stats:', error);
    }
  };

  const fetchGoals = async () => {
    try {
      const res = await API.get('/therapist/goals');
      setGoals(res.data || {});
    } catch (err) {
      console.error('Error fetching therapist goals:', err);
    }
  };

  // --------------------------------------------------------------------------
  // LOAD THERAPIST NAME FROM LOCALSTORAGE
  // --------------------------------------------------------------------------
  const loadTherapistName = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setTherapistName(`${user.first_name} ${user.last_name}` || '');
      setCurrentUserId(user.id || null);
    }
  };

  // --------------------------------------------------------------------------
  // FETCH THERAPIST PROFILE
  // --------------------------------------------------------------------------
  const fetchTherapistProfile = async () => {
    try {
      const res = await API.get('/therapist/profile');
      setProfile(res.data);
    } catch (error) {
      console.error('Error fetching therapist profile:', error);
    }
  };
  
  // --------------------------------------------------------------------------
  // FETCH TEAM MEMBERS
  // --------------------------------------------------------------------------
  const fetchTeamMembers = async () => {
    try {
      const res = await API.get('/therapist/team-profiles');
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  // --------------------------------------------------------------------------
  // useEffect (on mount)
  // --------------------------------------------------------------------------
  useEffect(() => {
    fetchSessionStats();
    fetchGoals();
    loadTherapistName();
    fetchTherapistProfile();
    fetchTeamMembers();
    fetchUnreadCount();
    // In a real app, we would also fetch trend data here
  }, []);

  // --------------------------------------------------------------------------
  // GREETING
  // --------------------------------------------------------------------------
  function getTimeOfDayGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }
  const greeting = `${getTimeOfDayGreeting()}, ${therapistName}!`;

  // --------------------------------------------------------------------------
  // TENURE AND CELEBRATION HELPERS
  // These helpers compute the tenure (years at practice based on hire_date)
  // and identify birthdays and work anniversaries occurring within the
  // current week.  They are memoized to avoid recalculating on every render.

  // Calculate years since a given date string. Returns null if date is empty or invalid.
  const yearsSince = (dateStr, onDate = new Date()) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    let years = onDate.getFullYear() - d.getFullYear();
    const hadAnniv =
      onDate.getMonth() > d.getMonth() ||
      (onDate.getMonth() === d.getMonth() && onDate.getDate() >= d.getDate());
    if (!hadAnniv) years -= 1;
    return Math.max(years, 0);
  };

  // Compute tenure for the logged-in therapist (derived from hire_date)
  const tenureYears = useMemo(() => yearsSince(profile?.hire_date || null), [profile?.hire_date]);

  // Helpers to determine the start and end of the current week (Monday to Sunday)
  const startOfWeek = (d = new Date()) => {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
    // Determine difference to Monday (or previous Monday if today is Sunday)
    const diff = (day === 0 ? -6 : 1) - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const endOfWeek = (d = new Date()) => {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(e.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  };

  // Given a date string (YYYY-MM-DD), return the date object representing
  // the same month/day in the current year. Handles Feb 29 on non-leap years by
  // mapping to Feb 28.
  const sameMonthDayThisYear = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const now = new Date();
    let candidate = new Date(now.getFullYear(), d.getMonth(), d.getDate());
    // If Feb 29 and non-leap year, adjust to Feb 28
    if (d.getMonth() === 1 && d.getDate() === 29 && candidate.getMonth() !== 1) {
      candidate = new Date(now.getFullYear(), 1, 28);
    }
    return candidate;
  };

  // Check whether a given date falls within a week range
  const inWeek = (dateObj, weekStart, weekEnd) => {
    if (!dateObj) return false;
    return dateObj >= weekStart && dateObj <= weekEnd;
  };

  // Build a list of celebration items for the current week.
  // Each item includes the event type ('birthday' or 'anniversary'), the date of
  // the occurrence, the name of the teammate, the number of years for
  // anniversaries, and an optional photo URL.
  const celebrationItems = useMemo(() => {
    const s = startOfWeek();
    const e = endOfWeek();
    return (teamMembers || []).flatMap((m) => {
      const items = [];
      // Birthday logic: check if teammate's dob occurs this week
      const bd = sameMonthDayThisYear(m?.dob);
      if (inWeek(bd, s, e)) {
        items.push({
          type: 'birthday',
          date: bd,
          name: `${m.first_name} ${m.last_name}`,
          years: null,
          photo: m.photo_url || null,
          userId: m.therapist_id,
          email: m.email,
        });
      }
      // Work anniversary logic: check if hire_date anniversary occurs this week
      const hd = sameMonthDayThisYear(m?.hire_date);
      if (inWeek(hd, s, e)) {
        items.push({
          type: 'anniversary',
          date: hd,
          name: `${m.first_name} ${m.last_name}`,
          years: yearsSince(m?.hire_date, new Date()),
          photo: m.photo_url || null,
          userId: m.therapist_id,
          email: m.email,
        });
      }
      return items;
    });
  }, [teamMembers]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* HEADER WITH GREETING */}
      <div style={headerStyle}>
        <h2 style={{ margin: 0 }}>{greeting}</h2>
        {unreadCount > 0 && (
          <span
            style={{
              backgroundColor: '#DC2626',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              marginLeft: '8px',
              fontSize: '0.85rem'
            }}
          >
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* CELEBRATION CARD: appears only when there are birthdays or work anniversaries this week */}
      {celebrationItems.length > 0 && (
        <CelebrationCard
          items={celebrationItems}
          currentUserId={currentUserId}
          onDrawCard={(item) => setCardTarget(item)}
        />
      )}

      {/* TOP ROW - PROFILE, STATS, TEAM */}
      <div style={topRowStyle}>
        {/* PROFILE CARD */}
        <ProfileCard 
          profile={{
            ...profile,
            // Pass derived tenure years to profile for display
            tenure_years: tenureYears,
          }}
          sessionStats={sessionStats}
          goals={goals}
        />

        {/* STATS CARD */}
        <StatsCard 
          sessionStats={sessionStats}
          navigate={navigate}
        />

        {/* TEAM CARD - Using existing component */}
        <TeamCard 
          teamMembers={teamMembers}
        />
      </div>

      {/* MIDDLE ROW - QUICK ACTIONS */}
      <div style={middleRowStyle}>
        <QuickActionsBar
          navigate={navigate}
        />
      </div>

      {/* SCHEDULE */}
      <ScheduleCard />

      {/* COMMUNITY CANVAS */}
      <CommunityCanvas />

      {cardTarget && (
        <BirthdayCardModal
          recipientId={cardTarget.userId}
          recipientFirstName={cardTarget.name.split(' ')[0]}
          type={cardTarget.type}
          onClose={() => setCardTarget(null)}
        />
      )}
    </div>
  );
}

// STYLES
const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
};

const topRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginBottom: '24px'
};

const middleRowStyle = {
  marginBottom: '24px'
};

const chartsContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: '1px solid #E5E7EB',
  marginBottom: '24px'
};

// --------------------------------------------------------------------------
// CRAYON_PALETTE — 52 Crayola-style colors (mirrored from CommunityCanvas; kept independent)
const CRAYON_PALETTE = [
  '#CC0000','#FF3333','#FF6666','#FF9999',
  '#FF007F','#FF69B4','#FF1493','#FFB6C1',
  '#FF6600','#FF8C00','#FFA040','#FFB347',
  '#FFD700','#FFE066','#FFFF00','#FFFACD',
  '#9ACD32','#ADFF2F','#7FFF00','#32CD32',
  '#008000','#228B22','#00CC00','#00FA9A',
  '#20B2AA','#008B8B','#00CED1','#00FFFF',
  '#87CEEB','#00BFFF','#1E90FF','#4169E1',
  '#0000FF','#0000CD','#000080','#191970',
  '#8A2BE2','#9400D3','#9370DB','#8B008B',
  '#EE82EE','#DDA0DD','#DA70D6','#FF00FF',
  '#8B4513','#D2691E','#CD853F','#F4A460',
  '#000000','#555555','#AAAAAA','#FFFFFF',
];

// --------------------------------------------------------------------------
// BirthdayCardModal
function BirthdayCardModal({ recipientId, recipientFirstName, type, onClose }) {
  const [selectedColor, setSelectedColor] = useState(CRAYON_PALETTE[0]);
  const [brushSize, setBrushSize]         = useState(6);
  const [note, setNote]                   = useState('');
  const [sending, setSending]             = useState(false);
  const [sent, setSent]                   = useState(false);
  const [error, setError]                 = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const canvasRef    = useRef(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fffef7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX;
    const clientY = e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  }

  function handlePointerDown(e) {
    e.preventDefault();
    isDrawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function handlePointerMove(e) {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  function handlePointerUp() {
    isDrawingRef.current = false;
  }

  async function handleSend() {
    setSending(true);
    setError('');
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const imageData = dataUrl.split(',')[1];
      await API.post('/therapist/birthday-card', {
        recipientId,
        type,
        imageData,
        note: note.trim(),
      });
      setSent(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send card. Please try again.');
    } finally {
      setSending(false);
    }
  }

  const isBirthday = type === 'birthday';
  const emoji = isBirthday ? '🎂' : '🎉';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, padding: 24, width: '100%',
          maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {sent ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1D2C5F' }}>
              Sent! {recipientFirstName} will love it 🎉
            </div>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px', color: '#1D2C5F' }}>
              {emoji} Draw a card for {recipientFirstName}
            </h3>

            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setShowColorPicker(v => !v)}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: selectedColor, border: '3px solid #1D2C5F',
                  cursor: 'pointer', flexShrink: 0,
                }}
              />
              <span
                style={{ fontSize: 11, color: '#6B7280', cursor: 'pointer' }}
                onClick={() => setShowColorPicker(v => !v)}
              >
                ▼ Color
              </span>
            </div>

            {showColorPicker && (
              <div
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 16,
                }}
                onClick={() => setShowColorPicker(false)}
              >
                <div
                  style={{
                    background: '#fff', borderRadius: 12, padding: 16,
                    width: '100%', maxWidth: 320,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ fontWeight: 700, marginBottom: 12, color: '#1D2C5F' }}>
                    Pick a color
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8 }}>
                    {CRAYON_PALETTE.map(c => (
                      <button
                        key={c}
                        onClick={() => { setSelectedColor(c); setShowColorPicker(false); }}
                        style={{
                          width: '100%', aspectRatio: '1', borderRadius: 6,
                          background: c,
                          border: c === selectedColor ? '3px solid #1D2C5F' : '1px solid #e5e7eb',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
              {[{ label: 'S', size: 3 }, { label: 'M', size: 6 }, { label: 'L', size: 12 }].map(({ label, size }) => (
                <button
                  key={size}
                  onClick={() => setBrushSize(size)}
                  style={{
                    padding: '4px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                    background: brushSize === size ? '#1D2C5F' : '#f3f4f6',
                    color: brushSize === size ? '#fff' : '#374151',
                    border: '1px solid ' + (brushSize === size ? '#1D2C5F' : '#e5e7eb'),
                    fontWeight: brushSize === size ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <canvas
              ref={canvasRef}
              width={480}
              height={260}
              style={{
                width: '100%', aspectRatio: '480/260', borderRadius: 8,
                border: '1px solid #e5e7eb', background: '#fffef7',
                display: 'block', touchAction: 'none', cursor: 'crosshair',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />

            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Add a personal note… (optional)"
              style={{
                width: '100%', marginTop: 12, padding: '8px 12px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 14, resize: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />

            {error && (
              <div style={{ color: '#DC2626', fontSize: 13, marginTop: 6 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={handleSend}
                disabled={sending}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 15, fontWeight: 700,
                  background: sending ? '#9ca3af' : '#1D2C5F', color: '#fff',
                  border: 'none', cursor: sending ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? 'Sending…' : 'Send Card'}
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 15,
                  background: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// CelebrationCard Component
// This component renders a celebratory card for birthdays and work anniversaries
// occurring within the current week. It accepts a list of items, where each
// item specifies the type ('birthday' or 'anniversary'), the person's name,
// an optional number of years for anniversaries, and an optional photo URL. If
// a photo is not provided, the card displays the person's initials instead.
function CelebrationCard({ items, currentUserId, onDrawCard }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #fce7f3 0%, #dbf4ff 100%)',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: 16,
        marginBottom: 24,
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>🎉 Celebrations This Week</h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((it, idx) => (
          <div
            key={idx}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                overflow: 'hidden',
                background: '#f3f4f6',
                flexShrink: 0,
              }}
            >
              {it.photo ? (
                <img
                  src={it.photo}
                  alt={it.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    fontWeight: 600,
                  }}
                >
                  {it.name
                    .split(' ')
                    .map((s) => s[0])
                    .join('')
                    .slice(0, 2)}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>
                {it.type === 'birthday'
                  ? `🎂 Happy Birthday, ${it.name}!`
                  : `🎉 Work Anniversary — ${it.name}`}
              </div>
              <div style={{ fontSize: 13, color: '#374151' }}>
                {it.type === 'anniversary' && typeof it.years === 'number'
                  ? `${
                      it.years === 0
                        ? 'First year!'
                        : `${it.years} year${it.years === 1 ? '' : 's'}`
                    } at the practice`
                  : 'This week'}
              </div>
              {Number(it.userId) !== Number(currentUserId) && onDrawCard && (
                <button
                  onClick={() => onDrawCard(it)}
                  style={{
                    marginTop: 8, padding: '4px 12px', borderRadius: 6, fontSize: 12,
                    background: '#1D2C5F', color: '#fff', border: 'none',
                    cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  🎨 Draw a card
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TherapistDashboard;